import { Flex, Text, Button, Box, Image } from "@chakra-ui/react";
import { RModal } from "components";
import { IconArrow, IconAvatarBg, IconMoneyBagBg, IconRewardsBg, IconRobotBg, IconStatsBg } from "icons";
import { useState, useCallback } from "react";

// Define a type for carousel slides
interface SlideData {
  header: string;
  description: string;
  content: React.ReactNode;
  icon: React.ReactNode;
  buttonText: string;
}

interface FuseGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  slides?: SlideData[];
}

// Sample data structure for slides
const defaultSlides: SlideData[] = [
  {
    header: 'SUIT UP, RACER!',
    description: 'Your avatar is your identity in Racino. Make it bold. Make it unforgettable.',
    content: 
    <Image src="/images/ftue/slide-1.png" alt="Slide" />,
    icon: <IconAvatarBg />,
    buttonText: 'CONTINUE'
  },
  {
    header: 'OUTSMART. NOT OUTDRIVE.',
    description: 'Pick your tires, tune your setup, and adapt to weather. Strategy wins races.',
    content: 
    <Image src="/images/ftue/slide-2.png" alt="Slide" />,
    icon: <IconStatsBg />,
    buttonText: 'CONTINUE'
  },
  {
    header: 'WE SIM. YOU WIN.',
    description: 'No driving required. Races run on your setup. Watch the action from any device.',
    content: 
    <Flex
      w="full"
      h="326px"
      borderRadius="0.5rem"
      position="relative"
      _before={{
        content: '""',
        position: 'absolute',
        inset: 0,
        border: '1.5px solid',
        background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.6) 0%, rgba(255, 255, 255, 0.12) 100%)',
        borderRadius: '0.5rem',
        padding: '2px',
        mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
        maskComposite: "exclude",
      }}
    >
      <Flex
       w="full"
       justifyContent="center"
       pt="1rem"
      >  
        <Text
          fontSize={{ base: '24px', md: '40px'}}
          lineHeight="100%"
          fontWeight={400}
          fontFamily="heading"
          background="linear-gradient(180deg, #8FAAED 0%, #718BE9 18%, #4F6DD9 73%, #4562CA 100%)"
          textShadow="0px 0px 8px 0px rgba(85, 115, 220, 0.4)"
          backgroundClip="text"
          textAlign="center"
        >
          YOUR CAR <br /> RACES FOR YOU
        </Text>
      </Flex>

      <Box 
        position="absolute"
        inset={0}
        background="linear-gradient(180deg, #000000 0%, rgba(0, 0, 0, 0) 70%)"
        zIndex={1}
        opacity={0.4}
        borderRadius="0.5rem"
      />
      <video
        width="100%"
        height="100%"
        controls={false}
        loop
        autoPlay
        muted
        playsInline
        style={{
          objectFit: 'cover',
          position: 'absolute',
          width: '100%',
          height: '100%',
          zIndex: -1,
          top: 0,
          left: 0,
          borderRadius: '0.5rem',
        }}
      >
        <source src="/videos/ftue-video.mp4" type="video/mp4" />
        Your browser does not support the video tag
      </video>
    </Flex>,
    icon: <IconRobotBg />,
    buttonText: 'CONTINUE'
  },
  {
    header: 'WIN REAL REWARDS.',
    description: 'Top racers earn real cash daily. Free races, prize pools, and big tournaments await.',
    content: 
    <Image src="/images/ftue/slide-4.png" alt="Slide" />,
    icon: <IconMoneyBagBg />,
    buttonText: 'REVEAL MY FREE CAR'
  },
  {
    header: 'YOU\'VE UNLOCKED YOUR FIRST CAR.',
    description: 'Unlock your first Racino car. You\'ll use this to race, upgrade, and climb the ranks.',
    content: 
    <Image src="/images/ftue/slide-5.png" alt="Slide" />,
    icon: <IconRewardsBg />,
    buttonText: 'JOIN YOUR FIRST RACE'
  },
];

const FtueModal = ({ 
  isOpen, 
  onClose,
  slides = defaultSlides 
}: FuseGuideModalProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const handlePrevious = useCallback(() => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  }, [slides.length]);

  const handleNext = useCallback(() => {
    if (currentSlide === slides.length - 1) {
      onClose();
    } else {
      setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
    }
  }, [slides.length, currentSlide, onClose]);

  return (
    <RModal
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      title={slides[currentSlide].header}
      icon={slides[currentSlide].icon}
      modalVariant="ftue"
      showCloseButton={false}
      footer={
        <Flex 
          justify="space-between" 
          w="full"
          gap="4"
        >
          {currentSlide > 0 && (
            <Button 
              variant="secondary-gradient"
              onClick={handlePrevious}
            >
              <IconArrow />
            </Button>
          )}
            
          
          <Button
            variant="unstyled"
            onClick={handleNext}
            w="full"
            borderRadius="0.25rem"
            background="linear-gradient(225deg, rgba(109, 241, 229, 0.2) 0%, rgba(79, 197, 186, 0.2) 18%, rgba(38, 166, 154, 0.2) 73%, rgba(26, 116, 107, 0.2) 100%)"
            position="relative"
            _before={{
              content: '""',
              position: 'absolute',
              inset: 0,
              borderRadius: '0.25rem',
              background: 'linear-gradient(360deg, rgba(76, 194, 183, 0.6) 0%, rgba(76, 194, 183, 0.24) 100%)',
              padding: '2px',
              mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
              maskComposite: "exclude",
              zIndex: -1,
            }}
            color="rgba(53, 255, 236, 0.8)"
            _hover={{
              background: "linear-gradient(360deg, rgba(76, 194, 183, 0.6) 0%, rgba(76, 194, 183, 0.24) 100%)",
              color: "rgba(53, 255, 236, 1)"
            }}
            transition="all 0.3s ease"
          >
            {slides[currentSlide].buttonText}
          </Button>
        </Flex>
      }
    >
      <Flex
        flexDir="column"
        gap="4"
      >
        {/* Carousel slide */}
        <Flex
          w="full"
          flexDir="column"
        >
          <Flex
            gap="0.5rem"
            w="full"
            alignItems="center"
            borderRadius="0.25rem"
            mb="0.5rem"
            px="1rem"
            py="0.5rem"
            background="linear-gradient(90deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)"
          >
            <Text
              fontSize={{ base: '16px', md: '20px'}}
              lineHeight="120%"
              fontWeight={600}
              color="white.80"
            >
              {slides[currentSlide].description}
            </Text>
          </Flex>
          {/* <Flex
            w="full"
            position="relative"
            p="1rem"
            _before={{
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.6) 0%, rgba(255, 255, 255, 0.12) 100%)',
              borderRadius: '0.25rem',
              padding: '2px',
              mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
              maskComposite: "exclude",
              zIndex: -1,
            }}
          >
          </Flex> */}
          {slides[currentSlide].content}

        </Flex>   
        {/* Carousel pagination and navigation */}
        <Flex justify="center" align="center" mt="2" gap="2">
          {/* Pagination dots */}
          <Flex gap="2">
            {slides.map((_, index) => (
              <Box
                key={`dot-${index}`}
                w="0.75rem"
                h="0.75rem"
                borderRadius="full"
                bg={currentSlide === index ? "white" : "rgba(255, 255, 255, 0.3)"}
                cursor="pointer"
              />
            ))}
          </Flex>
        </Flex>     
      </Flex>
    </RModal>
  );
};

export default FtueModal;